import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import ActionProposal from './ActionProposal';
import { Button } from '..';
import { ACTION_STATUS, APPROVABLE_ACTION_TYPES } from './utils/constants';
import './MessageRenderer.scss';

/**
 * Preprocess content to handle escaped characters
 */
function preprocessContent(content) {
  if (!content) return content;

  // Replace escaped newlines with actual newlines
  let processed = content.replace(/\\n/g, '\n');

  // Replace escaped tabs with actual tabs
  processed = processed.replace(/\\t/g, '\t');

  // Replace escaped quotes if needed
  processed = processed.replace(/\\"/g, '"');

  return processed;
}

/**
 * MessageRenderer - Enhanced message display with support for:
 * - Markdown formatting
 * - Code blocks with syntax highlighting
 * - Structured data (JSON, milestones, etc.)
 * - Lists and tables
 * - Interactive buttons
 * - Interactive template blanks
 * - Action proposals (Run/Skip approval flow)
 */
export const MessageRenderer = ({
  content,
  structuredData = null,
  buttons = null,
  onButtonClick = null,
  onTemplateClick = null,
  actionProposal = null,
  onActionRun = null,
  onActionSkip = null,
  actionStatus = ACTION_STATUS.PENDING,
  autoRunMode = 'off',
}) => {
  // Check if action proposal has any approvable actions
  const hasApprovableActions = (proposal) => {
    if (!proposal) return false;

    // Check proposal.type directly
    if (proposal.type && APPROVABLE_ACTION_TYPES.includes(proposal.type)) {
      return true;
    }

    // Check proposal.actions array for approvable types
    if (proposal.actions && Array.isArray(proposal.actions)) {
      return proposal.actions.some(
        (action) => action.type && APPROVABLE_ACTION_TYPES.includes(action.type)
      );
    }

    // Check if proposal has command or changes (legacy format)
    if (proposal.command || proposal.changes) {
      return true;
    }

    return false;
  };

  const shouldShowActionProposal =
    actionProposal &&
    hasApprovableActions(actionProposal) &&
    (actionStatus === ACTION_STATUS.PENDING ||
      actionStatus === ACTION_STATUS.COMPLETED);

  // Preprocess content to handle escaped characters
  const processedContent = preprocessContent(content);

  // Render structured data view if structuredData prop is provided
  if (structuredData) {
    return (
      <div className='message-renderer'>
        {/* Render conversational content */}
        {processedContent && (
          <div className='message-text'>
            <ReactMarkdown>{processedContent}</ReactMarkdown>
          </div>
        )}

        {/* Render action proposal */}
        {shouldShowActionProposal && (
          <ActionProposal
            proposal={actionProposal}
            onRun={onActionRun}
            onSkip={onActionSkip}
            status={actionStatus}
            autoRunMode={autoRunMode}
          />
        )}

        {/* Render structured data */}
        {structuredData.milestones && structuredData.milestones.length > 0 && (
          <MilestonesDisplay milestones={structuredData.milestones} />
        )}

        {structuredData.skills && structuredData.skills.length > 0 && (
          <SkillsDisplay skills={structuredData.skills} />
        )}

        {structuredData.budget && (
          <BudgetDisplay budget={structuredData.budget} />
        )}
      </div>
    );
  }

  // Default: render as markdown with optional buttons
  // Check if content has template blanks (_____)
  const hasTemplateBlanks =
    processedContent && processedContent.includes('_____');

  return (
    <div className='message-renderer'>
      {hasTemplateBlanks && onTemplateClick ? (
        <TemplateContent
          content={processedContent}
          onTemplateClick={onTemplateClick}
        />
      ) : (
        <>
          {/* Always render the text content */}
          {processedContent && (
            <div className='message-text'>
              <ReactMarkdown>{processedContent}</ReactMarkdown>
            </div>
          )}
          {/* Render action proposal below the text */}
          {shouldShowActionProposal && (
            <ActionProposal
              proposal={actionProposal}
              onRun={onActionRun}
              onSkip={onActionSkip}
              status={actionStatus}
              autoRunMode={autoRunMode}
            />
          )}
        </>
      )}
      {buttons && buttons.length > 0 && (
        <ButtonsDisplay buttons={buttons} onButtonClick={onButtonClick} />
      )}
    </div>
  );
};

/**
 * Display milestones in a structured card format
 */
const MilestonesDisplay = ({ milestones }) => {
  const totalAmount = milestones.reduce((sum, m) => sum + (m.amount || 0), 0);
  const totalDuration = milestones.reduce(
    (sum, m) => sum + (m.duration_days || 0),
    0
  );

  return (
    <div className='structured-data milestones-display'>
      <div className='structured-header'>
        <h4>📋 Project Milestones</h4>
        <div className='structured-meta'>
          <span className='meta-item'>
            💰 Total: ${totalAmount.toLocaleString()}
          </span>
          <span className='meta-item'>⏱️ {totalDuration} days</span>
        </div>
      </div>

      <div className='milestones-list'>
        {milestones.map((milestone, index) => (
          <div key={index} className='milestone-card'>
            <div className='milestone-header'>
              <span className='milestone-order'>Phase {milestone.order}</span>
              <h5 className='milestone-title'>{milestone.title}</h5>
            </div>

            <p className='milestone-description'>{milestone.description}</p>

            <div className='milestone-footer'>
              <div className='milestone-stat'>
                <span className='stat-label'>Budget</span>
                <span className='stat-value'>
                  ${milestone.amount?.toLocaleString() || 0}
                </span>
              </div>
              <div className='milestone-stat'>
                <span className='stat-label'>Duration</span>
                <span className='stat-value'>
                  {milestone.duration_days || 0} days
                </span>
              </div>
              {milestone.confidence && (
                <div className='milestone-stat'>
                  <span className='stat-label'>Confidence</span>
                  <span className='stat-value'>
                    {(milestone.confidence * 100).toFixed(0)}%
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Display skills as tags
 */
const SkillsDisplay = ({ skills }) => {
  return (
    <div className='structured-data skills-display'>
      <div className='structured-header'>
        <h4>🛠️ Required Skills</h4>
      </div>
      <div className='skills-tags'>
        {skills.map((skill, index) => (
          <span key={index} className='skill-tag'>
            {skill}
          </span>
        ))}
      </div>
    </div>
  );
};

/**
 * Display budget information
 */
const BudgetDisplay = ({ budget }) => {
  return (
    <div className='structured-data budget-display'>
      <div className='structured-header'>
        <h4>💰 Budget</h4>
      </div>
      <div className='budget-info'>
        <div className='budget-amount'>
          ${budget.value?.toLocaleString() || 0}
        </div>
        {budget.confidence && (
          <div className='budget-confidence'>
            Confidence: {(budget.confidence * 100).toFixed(0)}%
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Display interactive buttons
 */
const ButtonsDisplay = ({ buttons, onButtonClick }) => {
  return (
    <div className='buttons-display'>
      {buttons.map((button, index) => (
        <Button
          variant='secondary'
          key={index}
          onClick={() => onButtonClick && onButtonClick(button)}
          disabled={button.disabled}
        >
          {button.icon && <span className='button-icon'>{button.icon}</span>}
          <span className='button-text'>{button.label}</span>
        </Button>
      ))}
    </div>
  );
};

/**
 * Display content with interactive template blanks
 */
const TemplateContent = ({ content, onTemplateClick }) => {
  // Split content by lines and process each line
  const lines = content.split('\n');

  return (
    <div className='template-content'>
      {lines.map((line, lineIndex) => {
        // Check if line contains template blanks
        if (line.includes('_____')) {
          // Split by _____ and create clickable blanks
          const parts = line.split('_____');
          return (
            <div key={lineIndex} className='template-line'>
              {parts.map((part, partIndex) => (
                <React.Fragment key={partIndex}>
                  {part}
                  {partIndex < parts.length - 1 && (
                    <span
                      className='template-blank'
                      onClick={() => onTemplateClick(lineIndex, partIndex)}
                      role='button'
                      tabIndex={0}
                      aria-label={`Fill in blank ${partIndex + 1} on line ${
                        lineIndex + 1
                      }`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onTemplateClick(lineIndex, partIndex);
                        }
                      }}
                    >
                      _____
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          );
        }
        // Regular line without blanks
        return (
          <div key={lineIndex} className='template-line'>
            {line}
          </div>
        );
      })}
    </div>
  );
};

export default memo(MessageRenderer);
